import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, obj = logged in
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) return { mfa: "verify", mfaToken: data.mfa_token };
      if (data.mfa_setup_required) return { mfa: "setup", mfaToken: data.mfa_token };
      // Cookie is now set HttpOnly by the backend; we only keep the user object client-side.
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Intentionally ignored: client-side logout proceeds even if server call fails.
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout server call failed:", e?.message || e);
      }
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, login, logout, error, refresh: fetchMe }),
    [user, login, logout, error, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
